/**
 * Capacitor utilities for detecting native environment and handling Google Auth
 */

// Type definitions for the Capacitor Google Auth plugin
interface GoogleAuthUser {
  email: string;
  familyName: string;
  givenName: string;
  id: string;
  imageUrl: string;
  name: string;
  authentication: {
    accessToken: string;
    idToken: string;
    refreshToken?: string;
  };
}

interface GoogleAuthPlugin {
  signIn(): Promise<GoogleAuthUser>;
  signOut(): Promise<void>;
  refresh(): Promise<{ accessToken: string; idToken: string }>;
  initialize(options?: { clientId?: string; scopes?: string[]; grantOfflineAccess?: boolean }): Promise<void>;
}

// Type definitions for the SendIntent plugin
export interface SendIntentResult {
  title?: string;
  description?: string;
  type?: string;
  url?: string;
  additionalItems?: Array<{
    title?: string;
    url?: string;
    type?: string;
  }>;
}

interface SendIntentPlugin {
  checkSendIntentReceived(): Promise<SendIntentResult | null>;
  finish(): Promise<void>;
}

interface CapacitorPlugins {
  GoogleAuth?: GoogleAuthPlugin;
  SendIntent?: SendIntentPlugin;
}

interface CapacitorGlobal {
  isNativePlatform(): boolean;
  getPlatform(): string;
  Plugins: CapacitorPlugins;
}

declare global {
  interface Window {
    Capacitor?: CapacitorGlobal;
  }
}

/**
 * Check if the app is running in a Capacitor native environment (iOS/Android)
 */
export function isCapacitorNative(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.Capacitor !== undefined &&
    window.Capacitor.isNativePlatform()
  );
}

/**
 * Get the current platform
 */
export function getPlatform(): 'android' | 'ios' | 'web' {
  if (!isCapacitorNative()) {
    return 'web';
  }
  const platform = window.Capacitor?.getPlatform();
  if (platform === 'android') return 'android';
  if (platform === 'ios') return 'ios';
  return 'web';
}

/**
 * Initialize the Google Auth plugin (should be called once on app start)
 */
export async function initializeGoogleAuth(): Promise<void> {
  if (!isCapacitorNative()) {
    return;
  }

  const GoogleAuth = window.Capacitor?.Plugins?.GoogleAuth;
  if (!GoogleAuth) {
    console.warn('GoogleAuth plugin not available');
    return;
  }

  try {
    await GoogleAuth.initialize();
  } catch (error) {
    console.error('Failed to initialize GoogleAuth:', error);
  }
}

/**
 * Sign in with Google using the native Capacitor plugin
 * Returns the ID token that can be sent to the backend for verification
 */
export async function signInWithGoogleNative(): Promise<{
  idToken: string;
  user: {
    email: string;
    name: string;
    id: string;
  };
}> {
  if (!isCapacitorNative()) {
    throw new Error('Native Google Sign-In is only available in Capacitor');
  }

  const GoogleAuth = window.Capacitor?.Plugins?.GoogleAuth;
  if (!GoogleAuth) {
    throw new Error('GoogleAuth plugin not available');
  }

  try {
    const result = await GoogleAuth.signIn();
    
    if (!result.authentication?.idToken) {
      throw new Error('No ID token received from Google Sign-In');
    }

    return {
      idToken: result.authentication.idToken,
      user: {
        email: result.email,
        name: result.name,
        id: result.id,
      },
    };
  } catch (error) {
    // Handle specific error cases
    if (error instanceof Error) {
      // User cancelled the sign-in
      if (error.message.includes('canceled') || error.message.includes('cancelled')) {
        throw new Error('Sign-in was cancelled');
      }
      // Network error
      if (error.message.includes('network')) {
        throw new Error('Network error. Please check your connection.');
      }
    }
    throw error;
  }
}

/**
 * Sign out from Google (native only)
 */
export async function signOutFromGoogleNative(): Promise<void> {
  if (!isCapacitorNative()) {
    return;
  }

  const GoogleAuth = window.Capacitor?.Plugins?.GoogleAuth;
  if (!GoogleAuth) {
    return;
  }

  try {
    await GoogleAuth.signOut();
  } catch (error) {
    console.error('Failed to sign out from Google:', error);
  }
}

/**
 * Get the API base URL.
 * In Capacitor native, use the production API since we're running with bundled files.
 * In web, use relative URLs (handled by the dev server proxy or same-origin).
 * 
 * For local development, set VITE_API_BASE_URL to override (e.g., http://machina.local:3002)
 */
export function getApiBaseUrl(): string {
  // Check for dev override first (works in both native and web)
  const devApiUrl = import.meta.env.VITE_API_BASE_URL;
  if (devApiUrl) {
    return devApiUrl;
  }
  
  if (isCapacitorNative()) {
    return 'https://ping.nocreativity.com';
  }
  return '';
}

/**
 * Get the SendIntent plugin (lazy loaded to avoid issues on web)
 */
function getSendIntentPlugin(): SendIntentPlugin | null {
  // Access via Capacitor's plugin registry
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Capacitor = (window as any).Capacitor;
  if (!Capacitor?.Plugins?.SendIntent) {
    console.log('[ShareIntent] SendIntent not in Capacitor.Plugins');
    // Try to get it from the registered plugins
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const plugin = Capacitor?.registerPlugin?.('SendIntent');
    if (plugin) {
      console.log('[ShareIntent] Got plugin via registerPlugin');
      return plugin as SendIntentPlugin;
    }
    return null;
  }
  return Capacitor.Plugins.SendIntent as SendIntentPlugin;
}

/**
 * Check if there's a pending share intent (e.g., user shared a file to this app)
 */
export async function checkShareIntent(): Promise<SendIntentResult | null> {
  if (!isCapacitorNative()) {
    console.log('[ShareIntent] Not native platform, skipping');
    return null;
  }

  try {
    const SendIntent = getSendIntentPlugin();
    console.log('[ShareIntent] Plugin available:', !!SendIntent);
    
    if (!SendIntent) {
      console.warn('[ShareIntent] SendIntent plugin not available');
      return null;
    }
    
    const result = await SendIntent.checkSendIntentReceived();
    console.log('[ShareIntent] Received intent:', JSON.stringify(result, null, 2));
    return result;
  } catch (error) {
    console.error('[ShareIntent] Failed to check share intent:', error);
    return null;
  }
}

/**
 * Close the app and return to the sender after share intent is complete.
 * Call this after a successful upload when the app was opened via share intent.
 */
export async function finishShareIntent(): Promise<void> {
  if (!isCapacitorNative()) {
    return;
  }

  try {
    const SendIntent = getSendIntentPlugin();
    if (SendIntent) {
      console.log('[ShareIntent] Calling finish() to close app and return to sender');
      await SendIntent.finish();
    }
  } catch (error) {
    console.error('[ShareIntent] Failed to finish share intent:', error);
  }
}

/**
 * Convert a file:// or content:// URI to a WebView-accessible URL
 */
function convertToWebViewUrl(uri: string): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Capacitor = (window as any).Capacitor;
  if (Capacitor?.convertFileSrc) {
    const converted = Capacitor.convertFileSrc(uri);
    console.log('[ShareIntent] Converted URI:', uri, '->', converted);
    return converted;
  }
  return uri;
}

/**
 * Helper to delay execution
 */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function extensionFromMimeType(mimeType: string): string {
  const normalizedMimeType = mimeType.toLowerCase();

  switch (normalizedMimeType) {
    case 'image/jpeg':
    case 'image/jpg':
      return 'jpg';
    case 'image/heic':
    case 'image/heic-sequence':
      return 'heic';
    case 'image/heif':
    case 'image/heif-sequence':
      return 'heif';
    case 'video/quicktime':
      return 'mov';
    default:
      return normalizedMimeType.split('/')[1]?.replace(/[^a-z0-9]/g, '') || 'bin';
  }
}

/**
 * Read file from URL with retry logic
 * Returns ArrayBuffer of file content
 */
async function readFileWithRetry(
  webViewUrl: string,
  maxRetries: number = 3,
  delayMs: number = 100
): Promise<ArrayBuffer> {
  let lastError: Error | null = null;
  let lastSize = 0;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    // Wait before reading to allow disk sync (send-intent plugin may have buffered writes)
    if (attempt > 1) {
      console.log(`[ShareIntent] Retry ${attempt}/${maxRetries} after ${delayMs * attempt}ms delay`);
      await delay(delayMs * attempt);
    } else {
      // Initial delay to allow disk sync after file copy
      await delay(50);
    }
    
    try {
      const arrayBuffer = await new Promise<ArrayBuffer>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', webViewUrl, true);
        xhr.responseType = 'arraybuffer';
        
        xhr.onload = () => {
          // Status 0 is valid for file:// URLs in some WebView implementations
          if (xhr.status === 200 || xhr.status === 0) {
            resolve(xhr.response);
          } else {
            reject(new Error(`Failed to load file: ${xhr.status}`));
          }
        };
        
        xhr.onerror = () => {
          reject(new Error('XHR error loading file'));
        };
        
        xhr.send();
      });
      
      const currentSize = arrayBuffer.byteLength;
      console.log(`[ShareIntent] Attempt ${attempt}: Read ${currentSize} bytes`);
      
      // If we got a larger size than before, the file might still be writing
      if (currentSize > lastSize) {
        lastSize = currentSize;
        
        // If not the last retry, check if file is still growing
        if (attempt < maxRetries) {
          await delay(delayMs);
          continue;
        }
      }
      
      // Validate we got actual content
      if (currentSize < 100) {
        throw new Error(`File too small: ${currentSize} bytes`);
      }
      
      // Quick validation: check that the buffer has non-zero content at various points
      const view = new Uint8Array(arrayBuffer);
      const checkPoints = [0, Math.floor(currentSize / 4), Math.floor(currentSize / 2), Math.floor(3 * currentSize / 4), currentSize - 1];
      let hasContent = false;
      for (const idx of checkPoints) {
        if (idx < view.length && view[idx] !== 0) {
          hasContent = true;
          break;
        }
      }
      
      if (!hasContent) {
        console.warn('[ShareIntent] File appears to be all zeros, retrying...');
        lastError = new Error('File content appears empty');
        continue;
      }
      
      return arrayBuffer;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      console.warn(`[ShareIntent] Attempt ${attempt} failed:`, lastError.message);
    }
  }
  
  throw lastError || new Error('Failed to read file after retries');
}

/**
 * Convert a content URI to a File object
 * On Android, shared files come as content:// URIs that need to be converted
 * 
 * Uses XMLHttpRequest with ArrayBuffer for reliable binary file reading.
 * Includes retry logic to handle cases where the file is still being written
 * by the send-intent plugin (disk sync delays).
 */
export async function contentUriToFile(uri: string, mimeType: string): Promise<File | null> {
  console.log('[ShareIntent] Converting URI to file:', uri, 'mimeType:', mimeType);
  
  if (!isCapacitorNative()) {
    console.log('[ShareIntent] Not native, cannot convert URI');
    return null;
  }

  try {
    // Convert file:// URL to a WebView-accessible URL
    const webViewUrl = convertToWebViewUrl(uri);
    console.log('[ShareIntent] Loading content from WebView URL:', webViewUrl);
    
    // Read file with retry logic to handle disk sync delays
    const arrayBuffer = await readFileWithRetry(webViewUrl, 3, 150);
    console.log('[ShareIntent] Got ArrayBuffer, size:', arrayBuffer.byteLength);
    
    // Create blob from ArrayBuffer with explicit mime type
    const blob = new Blob([arrayBuffer], { type: mimeType });
    
    // Generate a filename based on the mime type
    const extension = extensionFromMimeType(mimeType);
    const filename = `shared_${Date.now()}.${extension}`;
    
    const file = new File([blob], filename, { type: mimeType });
    console.log('[ShareIntent] Created file:', filename, 'size:', file.size);
    return file;
  } catch (error) {
    console.error('[ShareIntent] Failed to convert content URI to file:', error);
    return null;
  }
}

/**
 * Determine content type from MIME type
 */
export function getContentTypeFromMime(mimeType: string): 'photo' | 'video' | null {
  if (mimeType.startsWith('image/')) {
    return 'photo';
  }
  if (mimeType.startsWith('video/')) {
    return 'video';
  }
  return null;
}

// Type definitions for the App plugin
interface AppInfo {
  name: string;
  id: string;
  build: string;
  version: string;
}

interface AppState {
  isActive: boolean;
}

interface AppPlugin {
  getInfo(): Promise<AppInfo>;
  addListener(eventName: 'appStateChange', callback: (state: AppState) => void): Promise<{ remove: () => void }>;
}

/**
 * Get the App plugin (lazy loaded)
 */
function getAppPlugin(): AppPlugin | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Capacitor = (window as any).Capacitor;
  
  // Try Capacitor.Plugins.App first (older style)
  if (Capacitor?.Plugins?.App) {
    console.log('[AppPlugin] Found via Capacitor.Plugins.App');
    return Capacitor.Plugins.App as AppPlugin;
  }
  
  // Try registerPlugin (Capacitor 6 style)
  if (Capacitor?.registerPlugin) {
    try {
      const App = Capacitor.registerPlugin('App');
      if (App) {
        console.log('[AppPlugin] Found via registerPlugin');
        return App as AppPlugin;
      }
    } catch (e) {
      console.log('[AppPlugin] registerPlugin failed:', e);
    }
  }
  
  console.log('[AppPlugin] Plugin not found');
  return null;
}

/**
 * Get the current app version info
 * Returns versionCode (build number) and versionName
 */
export async function getAppVersion(): Promise<{ versionCode: number; versionName: string } | null> {
  if (!isCapacitorNative()) {
    return null;
  }

  try {
    const App = getAppPlugin();
    if (!App) {
      console.warn('[AppVersion] App plugin not available');
      return null;
    }

    const info = await App.getInfo();
    // In Capacitor, 'build' is the versionCode (build number) and 'version' is the versionName
    return {
      versionCode: parseInt(info.build, 10) || 1,
      versionName: info.version || '1.0',
    };
  } catch (error) {
    console.error('[AppVersion] Failed to get app version:', error);
    return null;
  }
}

/**
 * Listen for app state changes (foreground/background)
 * Returns a cleanup function to remove the listener
 */
export function onAppStateChange(callback: (isActive: boolean) => void): () => void {
  if (!isCapacitorNative()) {
    return () => {};
  }

  const App = getAppPlugin();
  if (!App) {
    console.warn('[AppState] App plugin not available');
    return () => {};
  }

  let listenerHandle: { remove: () => void } | null = null;

  try {
    const result = App.addListener('appStateChange', (state: AppState) => {
      console.log('[AppState] State changed:', state.isActive ? 'active' : 'inactive');
      callback(state.isActive);
    });

    // Handle both Promise and direct return value (Capacitor 6 compatibility)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const anyResult = result as any;
    if (anyResult && typeof anyResult.then === 'function') {
      anyResult.then((handle: { remove: () => void }) => {
        listenerHandle = handle;
      }).catch((error: unknown) => {
        console.error('[AppState] Failed to add listener:', error);
      });
    } else if (anyResult && typeof anyResult.remove === 'function') {
      // Direct handle returned
      listenerHandle = anyResult;
    }
  } catch (error) {
    console.error('[AppState] Failed to add listener:', error);
  }

  // Return cleanup function
  return () => {
    if (listenerHandle) {
      listenerHandle.remove();
    }
  };
}
